import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TerraIconModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

import { MeetingPrepPanelService } from '../meeting-prep-panel/meeting-prep-panel.service';

// DEV-26282: feedback widget at the panel bottom. The rating logs immediately on thumb press;
// the comment is an optional secondary submit (per the Figma dev notes).
@Component({
  selector: 'ninety-meeting-prep-feedback',
  templateUrl: './meeting-prep-feedback.component.html',
  styleUrl: './meeting-prep-feedback.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TerraIconModule, ButtonComponent],
})
export class MeetingPrepFeedbackComponent {
  private readonly panelService = inject(MeetingPrepPanelService);

  readonly rated = output<boolean>();
  readonly submitted = output<{ helpful: boolean; comment: string }>();

  protected readonly helpful = computed(() => this.panelService.feedback().helpful);
  protected readonly expanded = computed(() => this.panelService.feedback().expanded);
  protected readonly comment = computed(() => this.panelService.feedback().comment);
  protected readonly thanked = computed(() => this.panelService.feedback().thanked);

  // Placeholder copy differs by rating (DEV-26282 Figma).
  protected readonly placeholder = computed(() =>
    this.helpful() ? 'Tell us more about what you liked...' : 'Please tell us how we can improve...'
  );

  protected rate(value: boolean): void {
    if (this.helpful() === value) return;
    this.panelService.patchFeedback({ helpful: value, expanded: true, thanked: false });
    this.rated.emit(value);
  }

  protected toggleExpanded(): void {
    if (this.helpful() === null) return;
    const open = !this.expanded();
    this.panelService.patchFeedback(open ? { expanded: true, thanked: false } : { expanded: false });
  }

  protected onCommentChange(value: string): void {
    this.panelService.patchFeedback({ comment: value });
  }

  protected submit(): void {
    const helpful = this.helpful();
    if (helpful === null) return;
    this.submitted.emit({ helpful, comment: this.comment().trim() });
    this.panelService.patchFeedback({ comment: '', expanded: false, thanked: true });
  }
}
